import React, { useState, createElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, DownloadIcon, ZoomInIcon, ZoomOutIcon, PrinterIcon } from 'lucide-react';
import { useTheme } from '../providers/ThemeProvider';
import { Navbar } from '../Navbar';
import { Footer } from '../Footer';
export const InventionNo15Viewer: React.FC = () => {
  const navigate = useNavigate();
  const {
    theme
  } = useTheme();
  const isDark = theme === 'dark';
  const [zoom, setZoom] = useState(100);
  // Detect mobile device
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  // PDF URL for Invention No. 15 BWV 786
  const pdfUrl = 'https://www.mutopiaproject.org/ftp/BachJS/BWV786/bach-invention-15/bach-invention-15-let.pdf';
  // Use mobile-friendly PDF parameters on mobile devices
  const pdfViewUrl = isMobile ? `${pdfUrl}#view=Fit&zoom=page-width` : `${pdfUrl}#view=FitH`;
  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 10, 200));
  };
  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 10, 50));
  };
  const handleDownload = () => {
    // Download the actual PDF
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = 'Bach - Invention No. 15 BWV 786.pdf';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const handlePrint = () => {
    window.print();
  };
  return <div className={`min-h-screen ${isDark ? 'bg-gradient-to-r from-[#0F1626] via-[#1E3A8A] to-[#2C3E50]' : 'bg-gray-50'}`} data-id="element-4717">
      <Navbar data-id="element-4718" />

      {/* Wrapper with top padding to account for fixed Navbar */}
      <div className="pt-20" data-id="element-4719">
        {/* Hero Banner with Image */}
        <div className="relative w-full" style={{
        minHeight: '320px'
      }} data-id="element-4720">
          <img src="https://images.unsplash.com/photo-1552422535-c45813c61732?w=400&h=300&fit=crop" alt="Invention No. 15" className="w-full h-64 md:h-80 object-cover" data-id="element-4721" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/30 to-black/70" data-id="element-4722" />
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8" data-id="element-4723">
            <div className="max-w-7xl mx-auto" data-id="element-4724">
              <button onClick={() => navigate(-1)} className="mb-4 flex items-center text-white/90 hover:text-white transition-colors" data-id="element-4725">
                <ArrowLeftIcon className="h-5 w-5 mr-2" data-id="element-4726" />
                Back to Library
              </button>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-2" data-id="element-4727">
                Invention No. 15 in B minor
              </h1>
              <p className="text-lg md:text-xl text-white/90" data-id="element-4728">BWV 786</p>
              <p className="text-base md:text-lg text-white/80 mt-1" data-id="element-4729">
                Johann Sebastian Bach
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className={`sticky top-20 z-10 ${isDark ? 'bg-navy-800/95 backdrop-blur-sm border-b border-white/10' : 'bg-white/95 backdrop-blur-sm border-b border-gray-200'} shadow-sm`} data-id="element-4730">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between" data-id="element-4731">
          <div className="flex items-center space-x-2" data-id="element-4732">
            {!isMobile && <>
                <button onClick={handleZoomOut} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700 text-white' : 'hover:bg-gray-100 text-gray-800'}`} aria-label="Zoom out" data-id="element-4733">
                  <ZoomOutIcon className="h-5 w-5" data-id="element-4734" />
                </button>
                <span className={`text-sm font-medium px-3 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4735">
                  {zoom}%
                </span>
                <button onClick={handleZoomIn} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700 text-white' : 'hover:bg-gray-100 text-gray-800'}`} aria-label="Zoom in" data-id="element-4736">
                  <ZoomInIcon className="h-5 w-5" data-id="element-4737" />
                </button>
              </>}
          </div>
          <div className="flex items-center space-x-2" data-id="element-4738">
            <button onClick={handlePrint} className={`flex items-center justify-center px-4 py-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700 text-white border border-navy-700' : 'hover:bg-gray-100 text-gray-800 border border-gray-200'}`} data-id="element-4739">
              <PrinterIcon className="h-4 w-4 sm:mr-2" data-id="element-4740" />
              <span className="hidden sm:inline" data-id="element-4741">Print</span>
            </button>
            <button onClick={handleDownload} className="flex items-center justify-center px-4 py-2 border-2 border-green-500 text-green-500 bg-transparent hover:bg-green-500/10 rounded-lg transition-colors" data-id="element-4742">
              <DownloadIcon className="h-4 w-4 sm:mr-2" data-id="element-4743" />
              <span className="hidden sm:inline" data-id="element-4744">Download PDF</span>
            </button>
          </div>
        </div>
      </div>

      {/* Sheet Music Viewer */}
      <main className="max-w-7xl mx-auto px-4 py-8" data-id="element-4745">
        <div className={`rounded-xl overflow-hidden shadow-lg ${isDark ? 'bg-navy-800' : 'bg-white'}`} data-id="element-4746">
          {/* PDF Viewer - Embedded */}
          <div className="w-full" style={{
          height: 'calc(100vh - 300px)',
          minHeight: '600px'
        }} data-id="element-4747">
            <iframe src={pdfViewUrl} className="w-full h-full" title="Invention No. 15 Sheet Music" style={{
            border: 'none',
            ...(isMobile ? {} : {
              transform: `scale(${zoom / 100})`,
              transformOrigin: 'top center',
              transition: 'transform 0.2s ease'
            })
          }} data-id="element-4748" />
          </div>
        </div>

        {/* Piece Information */}
        <div className={`mt-8 rounded-xl p-6 ${isDark ? 'bg-navy-800' : 'bg-white'} shadow-sm`} data-id="element-4749">
          <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4750">
            About This Piece
          </h2>
          <div className="grid md:grid-cols-2 gap-6" data-id="element-4751">
            <div data-id="element-4752">
              <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4753">
                Composition Details
              </h3>
              <dl className="space-y-2" data-id="element-4754">
                <div data-id="element-4755">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-4756">
                    Composer
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4757">
                    Johann Sebastian Bach
                  </dd>
                </div>
                <div data-id="element-4758">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-4759">
                    Year Composed
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4760">
                    c. 1720-1723
                  </dd>
                </div>
                <div data-id="element-4761">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-4762">
                    Form
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4763">
                    Two-Part Invention
                  </dd>
                </div>
                <div data-id="element-4764">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-4765">
                    Key
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4766">
                    B minor
                  </dd>
                </div>
                <div data-id="element-4767">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-4768">
                    Catalog
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4769">
                    BWV 786
                  </dd>
                </div>
              </dl>
            </div>
            <div data-id="element-4770">
              <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4771">
                Performance Notes
              </h3>
              <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-4772">
                The fifteenth and final of Bach's Two-Part Inventions, this
                piece in B minor serves as a fitting conclusion to the
                collection with its sophisticated character and expressive
                depth. Written as pedagogical works for his eldest son Wilhelm
                Friedemann, the Inventions demonstrate how to play cleanly in
                two voices and develop musical ideas systematically. This
                invention features a noble, contemplative subject that unfolds
                with remarkable elegance, creating a dialogue of profound
                musical conversation between the hands. The minor key lends the
                piece a serious, reflective quality, while the intricate voice
                leading and rich harmonic language require mature musicianship
                and careful attention to phrasing. As the culminating work of
                the collection, it exemplifies Bach's mastery of the form and
                his ability to combine technical instruction with deeply moving
                musical expression, making it a rewarding challenge for advanced
                students and a treasured piece for performers.
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer data-id="element-4773" />
    </div>;
};